// Guest identity for the widget session.
//
// Holds who the guest is (name / email / phone), captured as early as the
// identity sheet and as late as the checkout form, and whether they proved
// that identity with an email OTP. Three consumers:
//
//   * booking.ts hold wrappers — every new hold carries the identity via
//     setGuestForHolds, so an abandoned cart names its owner.
//   * setCartGuestContact — back-fills holds created before capture (and
//     combo holds, whose RPC takes no guest params).
//   * CheckoutView — prefills its form; a returning guest types nothing.
//
// The profile persists in localStorage (per browser, not per tenant — a
// person's name doesn't change between venues, and it is only ever what the
// guest themselves typed into a booking form).
//
// Sign-in is a CAPABILITY TOKEN, not a Supabase account (20260806000110).
// The guest proves they can read an inbox or a WhatsApp thread; the server
// hands back an opaque token scoped to this tenant, and the guest RPCs are
// gated on possession of it — the same model booking_access_tokens has used
// for manage-links since 20260423000001. Guests therefore never hold the
// `authenticated` role, which on this platform is publicly obtainable via
// self-serve venue signup and must never be confused with "staff".
//
// The token lives in sessionStorage: the widget mounts directly into the
// venue's page, so anything durable there is readable by every other script
// that page loads. Tab-scoped bounds a leak to the visit.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "../config";
import { getCartToken, setCartGuestContact, setGuestForHolds } from "./booking";

const STORAGE_KEY = "vt-guest-profile";
// Tenant-scoped: book.vertu.co.za serves EVERY tenant from one origin, so a
// bare key would offer venue A's token to venue B (where it is invalid, but
// the widget would still show "signed in" until the server refused it).
const tokenKey = (tenantId: string) => `vt-guest-token:${tenantId}`;

/** Profile fields the server returns from the token RPCs.
 *
 *  Only the contact the guest PROVED comes back (20260927100020): an email
 *  sign-in carries `email` and a null `phone`, a WhatsApp sign-in carries
 *  `phone` and a null `email`, and both names are null. The guest record a
 *  sign-in matches holds names and a second contact that were typed at
 *  checkouts, possibly by someone else, so the server sends none of it. The
 *  name fields stay in the type so an older reply still merges. */
type ServerProfile = {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  email?: string | null;
};

export interface GuestProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const EMPTY: GuestProfile = { firstName: "", lastName: "", email: "", phone: "" };

function loadStored(): GuestProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const p = JSON.parse(raw) as Partial<GuestProfile>;
    return {
      firstName: typeof p.firstName === "string" ? p.firstName : "",
      lastName: typeof p.lastName === "string" ? p.lastName : "",
      email: typeof p.email === "string" ? p.email : "",
      phone: typeof p.phone === "string" ? p.phone : "",
    };
  } catch {
    // Storage blocked (some in-app browsers) — session-only identity.
    return EMPTY;
  }
}

/** Merge a sign-in reply (or the token reader's) into the local profile.
 *  Local wins where the guest typed something this session and the server
 *  fills the blanks, except the email, where the proven address wins. A null
 *  field in the reply (the contact that was not proven, and the names, which
 *  the server no longer sends) never clears what the guest typed. */
export function mergeSignInProfile(prev: GuestProfile, server: ServerProfile): GuestProfile {
  const text = (v: string | null | undefined) => (typeof v === "string" ? v : "");
  return {
    firstName: prev.firstName || text(server.first_name),
    lastName: prev.lastName || text(server.last_name),
    email: text(server.email) || prev.email,
    phone: prev.phone || text(server.phone),
  };
}

export function hasContact(p: GuestProfile): boolean {
  return Boolean(p.email.trim() || p.phone.trim());
}

function fullName(p: GuestProfile): string | null {
  const n = `${p.firstName} ${p.lastName}`.trim();
  return n || null;
}

/** Where a sign-in code can be sent. WhatsApp works because WE send it, on
 *  the tenant's own number, through the outbox — no auth provider involved. */
export type GuestChannel = "email" | "whatsapp";

interface GuestContextValue {
  profile: GuestProfile;
  /** True once a code has been verified in this tab. */
  authed: boolean;
  /** The capability token, for RPCs that need proven identity. */
  token: string | null;
  /** Merge + persist + push onto the cart's holds. */
  saveProfile: (patch: Partial<GuestProfile>) => void;
  /** Send a 6-digit code. Always resolves ok — the server will not say
   *  whether the contact is on file, so nobody can probe who books here. */
  requestCode: (
    channel: GuestChannel,
    destination: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  /** True while the "Who's booking?" sheet is on screen. */
  sheetOpen: boolean;
  /** Ask for identity if we still need it, then run `after`. If the guest is
   *  already identified (or was already asked for this cart) `after` runs
   *  immediately, so callers never have to know which case they are in. */
  askIdentityThen: (after: () => void) => void;
  /** Let the next cart ask again. */
  resetIdentityAsk: () => void;
  /** Close the sheet and run whatever was waiting on it. */
  closeSheet: () => void;
  /** Redeem the code for a token, and prefill from the stored profile. */
  verifyCode: (
    channel: GuestChannel,
    destination: string,
    code: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => void;
}

const GuestContext = createContext<GuestContextValue | null>(null);

export function GuestProvider({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}) {
  const [profile, setProfile] = useState<GuestProfile>(loadStored);
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(tokenKey(tenantId));
    } catch {
      return null;
    }
  });
  const authed = token !== null;
  // Mirror for computing merged values OUTSIDE setState updaters: React may
  // re-invoke an updater (StrictMode, transition re-bases), so updaters must
  // stay pure — no storage writes or RPCs inside them.
  const profileRef = useRef(profile);
  profileRef.current = profile;
  // The sheet and whatever is queued behind it. Held here rather than in the
  // gate component so the SHELL can defer opening the cart drawer until the
  // guest is through the sheet — two overlays at once read as a broken screen.
  const [sheetOpen, setSheetOpen] = useState(false);
  const askedRef = useRef(false);
  const afterSheetRef = useRef<(() => void) | null>(null);

  // Keep the hold wrappers in sync from the first render on: a stored
  // profile means even the FIRST hold of a returning visitor carries their
  // identity, before any UI is shown.
  useEffect(() => {
    setGuestForHolds(
      hasContact(profile)
        ? {
            name: fullName(profile),
            email: profile.email.trim() || null,
            phone: profile.phone.trim() || null,
          }
        : null,
    );
  }, [profile]);

  const mergeServerProfile = useCallback(
    (server: ServerProfile) => {
      // See mergeSignInProfile. Computed from the ref so the setState updater
      // stays pure.
      const next = mergeSignInProfile(profileRef.current, server);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage blocked */
      }
      setProfile(next);
    },
    [],
  );

  // Same-tab return: a token still in sessionStorage re-prefills without a
  // fresh code. Also the place a stale/expired token gets dropped.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc("get_guest_profile_by_token", {
          p_token: token,
        });
        if (cancelled) return;
        const r = data as ServerProfile & { ok?: boolean };
        if (r?.ok) {
          mergeServerProfile(r);
        } else {
          // Expired or revoked — stop claiming to be signed in.
          try {
            sessionStorage.removeItem(tokenKey(tenantId));
          } catch {
            /* storage blocked */
          }
          setToken(null);
        }
      } catch {
        /* offline — keep the local profile, stay quiet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, tenantId, mergeServerProfile]);

  const saveProfile = useCallback((patch: Partial<GuestProfile>) => {
    const next = { ...profileRef.current, ...patch };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage blocked */
    }
    if (hasContact(next)) {
      // Back-fill the holds already sitting in the cart (best-effort).
      void setCartGuestContact({
        cartToken: getCartToken(),
        name: fullName(next),
        email: next.email.trim() || null,
        phone: next.phone.trim() || null,
      });
    }
    setProfile(next);
  }, []);

  const requestCode = useCallback(
    async (channel: GuestChannel, destination: string) => {
      const dest = destination.trim();
      if (!dest) return { ok: false, error: "Enter where we should send the code." };
      const { error } = await supabase.rpc("request_guest_code", {
        p_tenant_id: tenantId,
        p_channel: channel,
        p_destination: dest,
      });
      if (error) return { ok: false, error: error.message };
      // Deliberately no distinction between "sent" and "no such guest" — the
      // server does not tell us, so the UI cannot leak it either.
      return { ok: true };
    },
    [tenantId],
  );

  const verifyCode = useCallback(
    async (channel: GuestChannel, destination: string, code: string) => {
      const { data, error } = await supabase.rpc("verify_guest_code", {
        p_tenant_id: tenantId,
        p_channel: channel,
        p_destination: destination.trim(),
        p_code: code.trim(),
      });
      if (error) return { ok: false, error: error.message };
      const r = data as
        | ({ ok: true; token: string } & ServerProfile)
        | { ok: false; reason?: string };
      if (!r?.ok) {
        return {
          ok: false,
          error: "That code didn't match, or it has expired. Ask for a new one.",
        };
      }
      try {
        sessionStorage.setItem(tokenKey(tenantId), r.token);
      } catch {
        /* storage blocked — the token still works for this render */
      }
      setToken(r.token);
      mergeServerProfile(r as ServerProfile);
      return { ok: true };
    },
    [tenantId, mergeServerProfile],
  );

  /** Cart emptied (checkout or clear) — the next cart may ask again. */
  const resetIdentityAsk = useCallback(() => {
    askedRef.current = false;
  }, []);

  const askIdentityThen = useCallback((after: () => void) => {
    // Already know them, or already asked once for this cart: no interruption.
    if (hasContact(profileRef.current) || askedRef.current) {
      after();
      return;
    }
    askedRef.current = true;
    afterSheetRef.current = after;
    setSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    const after = afterSheetRef.current;
    afterSheetRef.current = null;
    // Runs on BOTH paths — Continue and dismiss. A guest who declines to
    // identify still gets their cart; the sheet only ever delays it.
    after?.();
  }, []);

  const signOut = useCallback(() => {
    try {
      sessionStorage.removeItem(tokenKey(tenantId));
    } catch {
      /* storage blocked */
    }
    setToken(null);
  }, [tenantId]);

  const value = useMemo(
    () => ({ profile, authed, token, sheetOpen, askIdentityThen, closeSheet, resetIdentityAsk,
              saveProfile, requestCode, verifyCode, signOut }),
    [profile, authed, token, sheetOpen, askIdentityThen, closeSheet, resetIdentityAsk,
     saveProfile, requestCode, verifyCode, signOut],
  );

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
}

export function useGuest(): GuestContextValue {
  const ctx = useContext(GuestContext);
  if (!ctx) throw new Error("useGuest must be used inside GuestProvider");
  return ctx;
}
